import { t } from 'app/i18l'
import { useRoutingContext } from 'app/routing_context'
import { EditNonAvailabilityScreenProps } from 'home/stack/types'
import { isEmpty } from 'lodash'
import { useAddNonAvailability } from 'non_availability/queries/add'
import { useNonAvailability } from 'non_availability/queries/list'
import { UpdateNonAvailabilityParams } from 'non_availability/queries/types'
import { mapNonAvailabilityToCells } from 'non_availability/utils'
import { useCallback, useLayoutEffect, useMemo, useState } from 'react'
import { errorAlert } from 'shared/alerts'
import { StatusBoundary } from 'shared/components/status_boundary'
import { showHeaderTitle } from 'shared/header/header_title'
import { useRecentStoresContext } from 'stores/context'
import { useStores } from 'stores/queries/list'
import { useUser } from 'users/queries/detail'
import { AddNonAvailabilityView } from './views'

export function EditNonAvailabilityScreen({
  navigation,
  route,
}: EditNonAvailabilityScreenProps) {
  useLayoutEffect(() => {
    showHeaderTitle(navigation, t('requestNonAvailability'))
  }, [navigation])

  const { handleNavigation } = useRoutingContext()
  const { data: user } = useUser()

  const { activeStoreId } = useRecentStoresContext()
  const { data: stores } = useStores(data => data.stores)
  const activeStore = stores?.find(s => s.id === activeStoreId)

  const nonAvailabilityQuery = useNonAvailability({
    storeId: activeStore?.id,
  })
  const addNonAvailabilityMutation = useAddNonAvailability()

  const initialParams: UpdateNonAvailabilityParams = useMemo(() => {
    return {
      userId: user?.id,
      storeId: activeStore?.id,
      nonAvailabilities: [],
    }
  }, [activeStore?.id, user?.id])

  const [nonAvailabilityParams, setNonAvailabilityParams] =
    useState<UpdateNonAvailabilityParams>(initialParams)

  const [loading, setLoading] = useState<boolean>(false)

  const onAddNonAvailability = useCallback(async () => {
    setLoading(true)
    try {
      await addNonAvailabilityMutation.mutateAsync({
        ...nonAvailabilityParams,
        userId: user?.id,
        storeId: activeStore?.id,
      })
      setLoading(false)
      handleNavigation({
        routeName: 'schedule',
      })
    } catch (err: any) {
      setLoading(false)
      console.log(err)
      errorAlert(err, t('addAvailabilityErrorTitle'))
    }
  }, [
    activeStore?.id,
    addNonAvailabilityMutation,
    handleNavigation,
    nonAvailabilityParams,
    user?.id,
  ])

  const cells = useMemo(() => {
    return mapNonAvailabilityToCells(nonAvailabilityQuery.data?.nonAvailability)
  }, [nonAvailabilityQuery.data?.nonAvailability])

  return (
    <StatusBoundary
      isLoading={nonAvailabilityQuery.isLoading}
      error={nonAvailabilityQuery.error}
    >
      <AddNonAvailabilityView
        store={activeStore}
        cells={cells}
        onNonAvailabilityParamsChanged={setNonAvailabilityParams}
        onAddNonAvailability={onAddNonAvailability}
        nonAvailability={
          nonAvailabilityQuery.data
            ? !isEmpty(nonAvailabilityQuery.data.nonAvailability)
              ? nonAvailabilityQuery.data
              : undefined
            : undefined
        }
        loading={loading}
      />
    </StatusBoundary>
  )
}
